import React, { Suspense, lazy, useEffect } from 'react';
import Layout from './components/Layout';
import About from './components/About/About';
import NotFound from './components/NotFound';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './style.scss';
import { I18nextProvider } from 'react-i18next';
import SEO from './components/Seo';
import ConsentBanner from './components/ConsentBanner';
import i18n from './i18n';
import { PREFIXED_LANGUAGES, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, getLanguageFromPath, localizePath, consumeLanguageIntent } from './i18n/languages';

const Home = lazy(() => import('./components/Home'));
const CountryFullFinInfo = lazy(() => import('./components/FullFinInfo/CountryFullFinInfo'));
const EconomicIndicatorsMap = lazy(() => import('./components/EconomicIndicatorsMap'));
const Compare = lazy(() => import('./components/Compare'));
const ForecastAccuracy = lazy(() => import('./components/ForecastAccuracy'));
const CorrelationExplorer = lazy(() => import('./components/CorrelationExplorer'));
const UnderstandingGdpGuide = lazy(() => import('./components/guides/UnderstandingGdpGuide'));
const UnderstandingInflationGuide = lazy(() => import('./components/guides/UnderstandingInflationGuide'));
const CountryComparisonGuide = lazy(() => import('./components/guides/CountryComparisonGuide'));
const BlogPage = lazy(() => import('./components/blog/BlogPage'));
const UnderstandingUnemployment = lazy(() => import('./components/blog/UnderstandingUnemployment'));
const UnderstandingDebtToGdp = lazy(() => import('./components/blog/UnderstandingDebtToGdp'));
const UnderstandingExchangeRates = lazy(() => import('./components/blog/UnderstandingExchangeRates'));
const HowToCompareCountries = lazy(() => import('./components/blog/HowToCompareCountries'));
const WhatAreEconomicIndicators = lazy(() => import('./components/blog/WhatAreEconomicIndicators'));
const MacroPulsePage = lazy(() => import('./components/MacroPulse/MacroPulsePage'));
const MacroCalendarPage = lazy(() => import('./components/MacroCalendar/MacroCalendarPage'));
const MacroBriefingPage = lazy(() => import('./components/MacroBriefing/MacroBriefingPage'));
const StudioPage = lazy(() => import('./components/Studio/StudioPage'));

// Route table shared by the English root and every language prefix.
// English lives at /, other languages at /:lng/... - each version gets its
// own indexable URL.
const appRoutes = [
  { path: '/', element: <Home />, index: true },
  { path: 'map', element: <EconomicIndicatorsMap /> },
  { path: 'pulse', element: <MacroPulsePage /> },
  { path: 'compare', element: <Compare /> },
  { path: 'forecast-accuracy', element: <ForecastAccuracy /> },
  { path: 'countries/:code', element: <CountryFullFinInfo /> },
  { path: 'About', element: <About /> },
  { path: 'correlation', element: <CorrelationExplorer /> },
  { path: 'guides/understanding-gdp', element: <UnderstandingGdpGuide /> },
  { path: 'guides/understanding-inflation', element: <UnderstandingInflationGuide /> },
  { path: 'guides/country-comparison-methodology', element: <CountryComparisonGuide /> },
  { path: 'blog', element: <BlogPage /> },
  { path: 'blog/understanding-unemployment', element: <UnderstandingUnemployment /> },
  { path: 'blog/understanding-debt-to-gdp', element: <UnderstandingDebtToGdp /> },
  { path: 'blog/understanding-exchange-rates', element: <UnderstandingExchangeRates /> },
  { path: 'blog/how-to-compare-country-economies', element: <HowToCompareCountries /> },
  { path: 'blog/what-are-economic-indicators', element: <WhatAreEconomicIndicators /> },
  { path: 'calendar', element: <MacroCalendarPage /> },
  { path: 'briefing', element: <MacroBriefingPage /> },
  { path: 'studio/:code', element: <StudioPage /> },
];

const renderRoute = (route) =>
  route.index
    ? <Route key="index" index element={route.element} />
    : <Route key={route.path} path={route.path} element={route.element} />;

// Language aliases accepted by the legacy ?lng= query parameter.
const LANGUAGE_ALIASES = { uk: 'ua', zh: 'zh-CN', cn: 'zh-CN' };

// Keeps i18n language and the URL in sync. The URL prefix is the source of
// truth; unprefixed internal links redirect to the localized URL so the
// active language survives navigation. An explicit switch to the default
// (unprefixed) language is honored via the language intent flag.
const LanguageSync = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // i18next's query-string language detection produced ?lng=xx URLs, which
    // are duplicate addresses for content that already has a canonical
    // localized path. Fold them into that path with a replace so the index
    // consolidates on a single URL per language.
    const requested = new URLSearchParams(location.search).get('lng');
    if (requested) {
      const target = SUPPORTED_LANGUAGES.includes(requested)
        ? requested
        : LANGUAGE_ALIASES[requested];
      navigate(localizePath(location.pathname, target || DEFAULT_LANGUAGE), { replace: true });
      return;
    }

    const routeLang = getLanguageFromPath(location.pathname) || DEFAULT_LANGUAGE;
    const intent = consumeLanguageIntent();

    // An unprefixed URL while a non-English language is active means an
    // internal link without a language prefix was followed - redirect to the
    // localized URL so the user stays in their language.
    if (routeLang === DEFAULT_LANGUAGE && !intent && i18n.language !== DEFAULT_LANGUAGE) {
      navigate(localizePath(location.pathname, i18n.language), { replace: true });
      return;
    }

    const targetLanguage = intent || routeLang;
    if (targetLanguage !== i18n.language) {
      i18n.changeLanguage(targetLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
};

const App = () => (
  <>
    <SEO
      author="Anton Nalyvaiko"
    />

    <React.StrictMode>
      <Suspense fallback={<div className="my-spinner">Loading...</div>}>
        <I18nextProvider i18n={i18n}>
          <LanguageSync />
          <Layout>
            <Routes>
              {appRoutes.map(renderRoute)}

              {PREFIXED_LANGUAGES.map((lng) => (
                <Route key={lng} path={`/${lng}`}>
                  {appRoutes.map(renderRoute)}
                  <Route path="*" element={<NotFound />} />
                </Route>
              ))}

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
        </I18nextProvider>
      </Suspense>
    </React.StrictMode>
    <ConsentBanner />
  </>
);

export default App;
